<!-- eslint-disable @typescript-eslint/indent -->
<script lang="ts" setup>
import { ref, computed, inject, Ref } from 'vue';
import { useDebounce } from '@vueuse/core';
import { definitions } from '../Definitions';
import { DetailsBuilderContract } from '@/components/builder/details';
import { BlueprintDefinition } from '@/components/builder/details/blueprints/BlueprintDefinition';
import ComponentButton from '../helpers/ComponentButton.vue';
import { Drag } from '@/components/dragdrop';

const props = defineProps({
  "details": null,
  "isDocumentDetails": { type: Boolean,  },
  "documentFields": null
});

const emit = defineEmits(["add"]);

const search = ref('');
const debouncedSearch = useDebounce(search, 500);
const documentFieldsBase = inject<Ref<BlueprintDefinition[]>>('documentFields');
const documentFields = computed(() => props.documentFields != null ? props.documentFields : documentFieldsBase.value);

const mappedDocumentFields = computed(() =>
{
    if (!documentFields.value)
    {
        return [];
    }

    // Sprawdzamy, czy documentFields.value jest iterowalne
    if (!Array.isArray(documentFields.value) && typeof documentFields.value !== 'object')
    {
        return [];
    }

    let fields = [];

    try
    {
        // Jeśli to tablica obiektów BlueprintDefinition, użyjmy jej bezpośrednio
        if (Array.isArray(documentFields.value))
        {
            fields = documentFields.value.map((item: any, index) => ({
                position: index,
                disabled: false,
                type: 'logito-field',
                name: item.name || (item as any).headerName || '',
                group: 'fields',
            }));
        }
        else
        {
            // Jeśli to obiekt, konwertujemy na pary klucz-wartość
            fields = Object.entries(documentFields.value).map(([key, value]: [string, any], index) => ({
                position: index,
                disabled: false,
                type: 'logito-field',
                name: value?.headerName || key,
                group: 'fields',
            }));
        }
    }
    catch (error)
    {
        return [];
    }

    return fields;
});

const components = computed<BlueprintDefinition[]>(() => definitions);
const noResults = computed(
    () =>
        !documentFieldsSectionFiltered.value.length &&
        !groupedFieldsSection.value.length &&
        !systemFieldsSection.value.length &&
        !functionalitiesSection.value.length &&
        !systemFieldsSection.value.length
);

const documentFieldsSectionFiltered = computed(() =>
{
    if (!documentFields.value || !Array.isArray(documentFields.value))
    {
        return mappedDocumentFields.value.filter((item) =>
            item.name.toLowerCase().includes(debouncedSearch.value.toLowerCase().trim())
        );
    }

    return documentFields.value.filter((item) =>
        item.name.toLowerCase().includes(debouncedSearch.value.toLowerCase().trim())
    );
});

const documentFieldsSection = computed(() =>
{
    return documentFieldsSectionFiltered.value
        .filter((item) => item.group == 'form-fields')
        .reduce<Array<{ name: string; children: BlueprintDefinition[] }>>((acc, item) =>
        {
            const group = acc.find((p) => p.name == item.form);

            if (group) group.children.push(item);
            else if (item.form)
            {
                acc.push({
                    name: item.form,
                    children: [item],
                });
            }

            return acc;
        }, []);
});

const groupedFieldsSection = computed(() =>
{
    return documentFieldsSectionFiltered.value.filter((item) => item.group == 'grouped-fields');
});

const systemFieldsSection = computed(() =>
{
    return documentFieldsSectionFiltered.value.filter((item) => item.group == 'system-fields');
});

const systemElementsSection = computed(() =>
{
    return components.value
        .filter((p) => p.group == 'system-elements' && p.name.toLowerCase().includes(debouncedSearch.value.toLowerCase().trim()))
        .sort((a, b) => a.position - b.position);
});

const functionalitiesSection = computed(() =>
{
    return components.value
        .filter(
            (p) => p.group == 'functionalities' && p.name.toLowerCase().includes(debouncedSearch.value.toLowerCase().trim())
        )
        .sort((a, b) => a.position - b.position);
});

const onClick = (data: BlueprintDefinition) => emit('add', data);
</script>

<template>
    <div>
        <div class="input">
            <input v-model="search" type="text" class="form-control" :placeholder="$t('[[[Wyszukaj...]]]')" />
            <i class="icon fa-solid fa-magnifying-glass"></i>
        </div>
        <div v-if="documentFieldsSectionFiltered.length > 0 && isDocumentDetails">
            <h5 class="text-center py-2">{{ $t('[[[Pola formularzy]]]') }}</h5>
            <div class="row">
                <template v-for="definition in documentFieldsSectionFiltered" :key="definition.name">
                    <div class="col-6 mb-2">
                        <drag :transfer-data="definition" class="d-grid" :draggable="true">
                            <div class="btn btn-light btn-block text-start w-full" :title="definition.name">
                                <span class="text-nowrap text-truncate">
                                    {{ definition.name }}
                                </span>
                            </div>
                        </drag>
                    </div>
                    <!-- <component-button :definition="definition" :details="details" @click="onClick" /> -->
                </template>
            </div>
        </div>
        <dynamic-details-accordion v-else>
            <dynamic-details-accordion-item
                v-if="documentFieldsSection.length"
                icon="far fa-file"
                :header="$t('[[[Pola formularzy]]]')"
            >
                <dynamic-details-accordion smaller>
                    <dynamic-details-accordion-item
                        v-for="group in documentFieldsSection"
                        :header="`${group.name} (${group.children.length})`"
                        :key="group.name"
                    >
                        <div class="row row-cols-2 g-2 mb-2">
                            <template v-for="definition in group.children" :key="definition.name">
                                <ComponentButton
                                    v-if="definition.name && definition.name.trim() !== '' ||
                                        (!definition.nameDictionary || Object.keys(definition.nameDictionary).length > 0) ||
                                        (!definition.text || Object.keys(definition.text).length > 0)"
                                    :definition="definition"
                                    :details="details"
                                    @click="onClick"
                                />
                            </template>
                        </div>
                    </dynamic-details-accordion-item>
                </dynamic-details-accordion>
            </dynamic-details-accordion-item>
            <dynamic-details-accordion-item
                v-if="groupedFieldsSection.length"
                icon="far fa-layer-group"
                :header="$t('[[[Pola zgrupowane]]]')"
            >
                <div class="row row-cols-2 g-2 mb-2">
                    <template v-for="definition in groupedFieldsSection" :key="definition.name">
                        <ComponentButton
                            v-if="definition.name && definition.name.trim() !== '' &&
                                (!definition.nameDictionary || Object.keys(definition.nameDictionary).length > 0) &&
                                (!definition.text || Object.keys(definition.text).length > 0)"
                            :definition="definition"
                            :details="details"
                            @click="onClick"
                        />
                    </template>
                </div>
            </dynamic-details-accordion-item>
            <dynamic-details-accordion-item
                v-if="systemFieldsSection.length"
                icon="far fa-grid-2"
                :header="$t('[[[Pola systemowe]]]')"
                no-border
            >
                <div class="row row-cols-2 g-2 mb-2">
                    <template v-for="definition in systemFieldsSection" :key="definition.name">
                        <ComponentButton
                            v-if="definition.name && definition.name.trim() !== '' &&
                                (!definition.nameDictionary || Object.keys(definition.nameDictionary).length > 0) &&
                                (!definition.text || Object.keys(definition.text).length > 0)"
                            :definition="definition"
                            :details="details"
                            @click="onClick"
                        />
                    </template>
                </div>
            </dynamic-details-accordion-item>
            <dynamic-details-accordion-item
                v-if="systemElementsSection.length"
                icon="far fa-table-layout"
                :header="$t('[[[Elementu układu]]]')"
            >
                <div class="row row-cols-2 g-2 mb-2">
                    <template v-for="definition in systemElementsSection" :key="definition.name">
                        <ComponentButton
                            v-if="definition.name && definition.name.trim() !== '' &&
                                (!definition.nameDictionary || Object.keys(definition.nameDictionary).length > 0) &&
                                (!definition.text || Object.keys(definition.text).length > 0)"
                            :definition="definition"
                            :details="details"
                            @click="onClick"
                        />
                    </template>
                </div>
            </dynamic-details-accordion-item>
            <dynamic-details-accordion-item
                v-if="functionalitiesSection.length"
                icon="far fa-sparkles"
                :header="$t('[[[Funkcjonalności]]]')"
            >
                <div class="row row-cols-2 g-2 mb-2">
                    <template v-for="definition in functionalitiesSection" :key="definition.name">
                        <ComponentButton :definition="definition" :details="details" @click="onClick" />
                    </template>
                </div>
            </dynamic-details-accordion-item>
        </dynamic-details-accordion>
        <table v-if="noResults" class="w-100">
            <tr>
                <td class="text-center p-3 mb-3 no-results text-muted my-2">
                    {{ $t('[[[Brak wyników]]]') }}
                </td>
            </tr>
        </table>
    </div>
</template>

<style lang="scss" scoped>
.input {
    position: relative;
    margin-bottom: 10px;

    input {
        padding-left: 36px;
        padding: 8px 8px 8px 40px;
        line-height: 20px;
    }

    .icon {
        position: absolute;
        top: 11px;
        left: 12px;
        color: var(--bs-body-color);
        font-size: 1rem;
    }

    .clear {
        position: absolute;
        top: 12px;
        right: 12px;
    }
}
</style>
